/**
 * Validation and sanitization utilities
 */

export const validators = {
  /**
   * Validate email format
   */
  email: (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  /**
   * Validate URL format (must start with http:// or https://)
   */
  url: (url) => {
    if (!url) return true; // Optional field
    try {
      new URL(url);
      return url.startsWith('http://') || url.startsWith('https://');
    } catch {
      return false;
    }
  },

  /**
   * Validate phone number (minimum 10 digits)
   */
  phone: (phone) => {
    if (!phone) return true; // Optional field
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    const digitsOnly = phone.replace(/\D/g, '');
    return phoneRegex.test(phone) && digitsOnly.length >= 10;
  },

  /**
   * Sanitize string input to prevent XSS attacks
   */
  sanitize: (str) => {
    if (!str) return '';
    return str
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '')
      .trim();
  }
};

/**
 * Validate and sanitize card form data
 */
export const validateCardData = (data) => {
  const errors = {};

  // Full name is required
  if (!data.full_name || !data.full_name.trim()) {
    errors.full_name = 'Full name is required';
  }

  // Validate email if provided
  if (data.email && !validators.email(data.email)) {
    errors.email = 'Please enter a valid email address';
  }

  // Validate URLs if provided
  const urlFields = ['website', 'linkedin', 'twitter', 'instagram'];
  urlFields.forEach(field => {
    if (data[field] && !validators.url(data[field])) {
      errors[field] = 'Please enter a valid URL (must start with http:// or https://)';
    }
  });

  // Validate phone if provided
  if (data.phone && !validators.phone(data.phone)) {
    errors.phone = 'Please enter a valid phone number (minimum 10 digits)';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Sanitize all fields in card data
 */
export const sanitizeCardData = (data) => {
  const sanitized = {};
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string') {
      sanitized[key] = validators.sanitize(value);
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
};
